import React from 'react'
import { PostHogProvider, usePostHog } from 'posthog-js/react'
import useCurrentUser from '../../hooks/auth/useCurrentUser'
import { parseSubdomain } from '../../helpers/stringUtils'
import features from './features'
import { POSTHOG_API_HOST } from '../../constants'
type FeatureContextType = {
  isFeatureEnabled: (featureName: string) => boolean
}

const FeatureContext = React.createContext<FeatureContextType>({
  isFeatureEnabled: () => false,
})

// Component to identify users and handle feature flags
const FeatureInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const posthog = usePostHog()
  const { currentUser, loading } = useCurrentUser()
  // A counter rather than a boolean so that every flag reload triggers a re-render.
  // PostHog fires onFeatureFlags twice on login: once for the anonymous user on init,
  // and again after posthog.identify() resolves flags for the authenticated user.
  // A boolean would stay `true` on the second fire and React would bail out, leaving
  // consumers with stale flag values until a page refresh.
  const [flagVersion, setFlagVersion] = React.useState(0)
  const subdomain = React.useMemo(() => parseSubdomain(window.location.hostname), [])

  React.useEffect(() => {
    if (loading) return

    if (currentUser !== undefined) {
      posthog.identify(currentUser.id, {
        email: currentUser.email,
        name: currentUser.name,
        subdomain: subdomain,
        groupIds: currentUser.groups.map((group) => group.id),
        groupNames: currentUser.groups.map((group) => group.name),
      })
    } else {
      posthog.reset()
      posthog.register({ app_version: process.env.REACT_APP_VERSION })
      posthog.setPersonProperties({ subdomain: subdomain })
    }
  }, [currentUser, loading, posthog, subdomain])

  React.useEffect(() => {
    return posthog.onFeatureFlags(() => {
      setFlagVersion((v) => v + 1)
    })
  }, [posthog])

  const isFeatureEnabled = React.useCallback(
    (featureName: string) => {
      // Local-dev flag override: force flags on without PostHog so features gated
      // behind not-yet-rolled-out flags are testable locally. Always on for the
      // report flags; add more at runtime via
      // `localStorage.setItem('devFeatureFlags', 'flag_a,flag_b')`. No-op outside
      // development, so it can never affect staging/production.
      if (process.env.NODE_ENV === 'development') {
        const forced = [
          'ai_reports',
          'insight',
          'advanced_insights',
          ...(window.localStorage.getItem('devFeatureFlags') ?? '')
            .split(',')
            .map((flag) => flag.trim())
            .filter(Boolean),
        ]
        if (forced.includes(featureName)) {
          return true
        }
      }
      return flagVersion > 0 && (posthog?.isFeatureEnabled(featureName) ?? false)
    },
    [flagVersion, posthog],
  )

  return <FeatureContext.Provider value={{ isFeatureEnabled }}>{children}</FeatureContext.Provider>
}

type FeatureProviderProps = {
  children: React.ReactNode
}

const FeatureProvider: React.FC<FeatureProviderProps> = ({ children }) => {
  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_KEY || ''}
      options={{
        api_host: POSTHOG_API_HOST,
        loaded: (posthog) => {
          posthog.register({ app_version: process.env.REACT_APP_VERSION })
          if (process.env.NODE_ENV === 'development') posthog.debug()
        },
      }}
    >
      <FeatureInitializer>{children}</FeatureInitializer>
    </PostHogProvider>
  )
}

export const useFeatures = () => {
  const context = React.useContext(FeatureContext)
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider')
  }

  return {
    [features.nodaFiCheck]: context.isFeatureEnabled(features.nodaFiCheck),
    [features.survey]: context.isFeatureEnabled(features.survey),
    [features.externalOrganizations]: context.isFeatureEnabled(features.externalOrganizations),
    [features.insight]: context.isFeatureEnabled(features.insight),
    [features.procedures]: context.isFeatureEnabled(features.procedures),
    [features.purchaseOrders]: context.isFeatureEnabled(features.purchaseOrders),
    [features.duplicatePmsConfig]: context.isFeatureEnabled(features.duplicatePmsConfig),
    [features.pmsConfigUpdates]: context.isFeatureEnabled(features.pmsConfigUpdates),
    [features.advancedInsights]: context.isFeatureEnabled(features.advancedInsights),
    [features.duplicateLocationConfig]: context.isFeatureEnabled(features.duplicateLocationConfig),
    [features.pmPlanner]: context.isFeatureEnabled(features.pmPlanner),
    [features.goldenBroom]: context.isFeatureEnabled(features.goldenBroom),
    [features.aiReports]: context.isFeatureEnabled(features.aiReports),
    [features.archivedTickets]: context.isFeatureEnabled(features.archivedTickets),
    [features.integrationsTab]: context.isFeatureEnabled(features.integrationsTab),
    [features.procedureEntryStatsTableWeb]: context.isFeatureEnabled(
      features.procedureEntryStatsTableWeb,
    ),
    [features.companyRulesToggle]: context.isFeatureEnabled(features.companyRulesToggle),
    [features.aiProcedureTemplateGenerator]: context.isFeatureEnabled(
      features.aiProcedureTemplateGenerator,
    ),
    [features.insightOverride]: context.isFeatureEnabled(features.insightOverride),
    [features.procedureScoring]: context.isFeatureEnabled(features.procedureScoring),
    [features.procedureFileFieldType]: context.isFeatureEnabled(features.procedureFileFieldType),
    [features.procedureSignatureFieldType]: context.isFeatureEnabled(
      features.procedureSignatureFieldType,
    ),
    [features.bulkCreateTickets]: context.isFeatureEnabled(features.bulkCreateTickets),
    [features.conditionalFieldRequirements]: context.isFeatureEnabled(
      features.conditionalFieldRequirements,
    ),
    [features.slackIntegrations]: context.isFeatureEnabled(features.slackIntegrations),
    [features.enableOrchidWebToggle]: context.isFeatureEnabled(
      features.enableOrchidWebToggle,
    ),
    [features.videoAttachments]: context.isFeatureEnabled(features.videoAttachments),
  }
}

export default FeatureProvider
